<template>
  <div ref="contactsRoot" class="contacts d-flex flex-column ga-4">
    <div v-if="settings?.title" class="title" :class="[settings.align]">{{settings.title}}</div>
    <div class="contact-list d-flex flex-column ga-4">
      <template v-for="(contact, index) in contactsWithStats">
        <div v-for="item in contact.list" class="contact d-flex align-center ga-3" v-if="contact.list.length >= 1 && contact.list[0].text">
          <v-icon :icon="typeIcon(contact.type)" size="24" class="type-contact"/>
          <a class="text" :href="`mailto:${item.text}`" v-if="contact.type == 'mail'" @click.stop="emit('element-click', item.id)">{{item.text}}</a>
          <a class="text" :href="`tel:${item.text}`" v-else-if="contact.type == 'phone'" @click.stop="emit('element-click', item.id)">{{item.text}}</a>
          <a class="text" :href="formatLink(item.text)" v-else-if="contact.type == 'web'" @click.stop="emit('element-click', item.id)">{{item.text}}</a>
          <div class="text" @click.stop="emit('element-click', item.id)" v-else>
            {{item.text}}
          </div>
        </div>
        <div v-if="contact.type == 'address' && contact.list.length >= 1 && contact.list[0].text && !route.path.includes('/analytics')" class="map-wrapper">
          <div class="yandex-map" style="width: 100%; height: 260px;"></div>
        </div>
        <div v-if="route.path.includes('/analytics') && contact.list.length >= 1 && contact.list[0].text" class="text-left mt-1">
          {{ contact?.list[index]?.clicks ?? 0 }} {{ $t('кликов') }} {{ contact?.list[index]?.uniqueClicks ?? 0 }} {{ $t('уникальных') }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import AddressIcon from "~/assets/icons/contacts/address.vue";
import MailIcon from "~/assets/icons/contacts/mail.vue";
import PhoneIcon from "~/assets/icons/contacts/phone.vue";
import WebIcon from "~/assets/icons/contacts/web.vue";
import TimeIcon from "~/assets/icons/contacts/time.vue";
import {computed, onMounted, onUnmounted, ref} from "vue";
import {loadYandexApi} from "@/services/yandex-loader";
import {useRoute} from "vue-router";
import {useExternalLink} from "~/composables/useExternalLink";

const props = defineProps({
  settings: { type: Object },
  componentFullData: { type: Object }
})

const route = useRoute()
const { formatLink } = useExternalLink()

const contactsWithStats = computed(() => {
  const contacts = props.settings?.contacts ?? []
  const stats = props.componentFullData?.clicksByCustomUniqueId ?? []

  let statIndex = 0

  return contacts.map((contact: any) => {
    const newList = contact.list.map((item: any) => {
      const stat = stats[statIndex]?.data
      statIndex++

      return {
        ...item,
        clicks: stat?.clicks ?? 0,
        uniqueClicks: stat?.uniqueClicks ?? 0
      }
    })

    return {
      ...contact,
      list: newList
    }
  })
})

const emit = defineEmits<{
  (e: 'element-click', id: string | number): void
}>()

const typeIcon = (type) => {
  switch (type) {
    case 'address':
      return AddressIcon
    case 'mail':
      return MailIcon
    case 'phone':
      return PhoneIcon
    case 'web':
      return WebIcon
    case 'time':
      return TimeIcon
  }
}


let myMap = null
let mapResizeObserver: ResizeObserver | null = null
const contactsRoot = ref<HTMLElement | null>(null)

const syncMapSize = () => {
  myMap?.container.fitToViewport()
}

const observeMapSize = (mapContainer: HTMLElement) => {
  mapResizeObserver?.disconnect()
  mapResizeObserver = new ResizeObserver(syncMapSize)
  mapResizeObserver.observe(mapContainer)

  requestAnimationFrame(syncMapSize)
}

const config = useRuntimeConfig()
const initMap = async () => {
  const ymaps = await loadYandexApi(config.public.yandexApi);
  if (typeof ymaps === 'undefined') return;

  const mapContainer = contactsRoot.value?.querySelector<HTMLElement>('.yandex-map')
  if (!mapContainer) return;

  ymaps.ready(() => {
    myMap = new ymaps.Map(mapContainer, {
      center: [51.1282, 71.4305],
      zoom: 10,
      controls: ['zoomControl', 'fullscreenControl']
    });

    renderPoints(ymaps);
    observeMapSize(mapContainer);
  });
};

const renderPoints = async (ymaps) => {
  if (!myMap) return;

  myMap.geoObjects.removeAll();

  const geoObjects = [];

  for (const contact of props.settings.contacts) {
    if(contact.type != 'address') continue;
    for (const item of contact.list) {
      try {
        const res = await ymaps.geocode(item.text);
        const firstGeoObject = res.geoObjects.get(0);


        if (firstGeoObject) {
          firstGeoObject.options.set('preset', 'islands#greenDotIcon');
          firstGeoObject.properties.set('balloonContent', item.text);

          myMap.geoObjects.add(firstGeoObject);
          geoObjects.push(firstGeoObject);
        }
      } catch (e) {
        console.error(`Ошибка геокодирования адреса ${item.text}:`, e);
      }
    }
  }

  if (geoObjects.length > 0) {
    myMap.setBounds(myMap.geoObjects.getBounds(), {
      checkZoomRange: true,
      zoomMargin: 30
    });
  }
}

onMounted(async () => {
  let addressExist = false;
  for (const contact of props.settings.contacts) {
    if(contact.type == 'address' && contact.list.length > 0) addressExist = true;
  }

  if(addressExist) {
    await initMap();
  }
})

onUnmounted(() => {
  mapResizeObserver?.disconnect();
  mapResizeObserver = null;

  if (myMap) {
    myMap.destroy();
    myMap = null;
  }
});

</script>

<style scoped lang="sass">
.contacts
  .title
    color: #363636
    font-size: 20px
    font-style: normal
    font-weight: 600
    line-height: normal
    width: 100%
    overflow: hidden
    text-overflow: ellipsis
    &.center
      text-align: center
  .contact-list
    .contact
      .text
        color: #363636
        font-size: 16px
        font-weight: 400
        line-height: normal
        white-space: pre-wrap
        overflow: hidden
        text-overflow: ellipsis
        text-decoration: none
</style>
